import { motion } from "framer-motion";
import { Cake, PartyPopper, Briefcase, BedDouble, Home, Star, Check } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import InquiryForm from "@/components/InquiryForm";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const OCCASIONS = [
  { icon: Cake, title: "Compleanni", text: "Torte, buffet salati, brunch di festa. La parte buona del compleanno, insomma." },
  { icon: PartyPopper, title: "Feste private", text: "Cene tra amici, ricorrenze, scuse per stare insieme. Piccole, belle, ben mangiate." },
  { icon: Briefcase, title: "Piccoli eventi aziendali", text: "Catering informali per team che vogliono mangiare bene senza noiosi buffet da convention." },
  { icon: BedDouble, title: "Ospiti di B&B", text: "Colazioni e brunch per chi affitta camere e vuole coccolare gli ospiti davvero." },
  { icon: Home, title: "Case vacanza", text: "Arrivi in Riviera e trovi la tavola pronta. Non male come inizio vacanza." },
  { icon: Star, title: "Occasioni speciali", text: "Proposte, anniversari, sorprese. Ci piace essere complici." },
];

const APPROACH = [
  "Piccoli numeri: lavoriamo bene con gruppi raccolti, non con i matrimoni da duecento",
  "Menu costruito insieme, seguendo stagione, gusti ed esigenze (vegetariano e vegano inclusi)",
  "Cuciniamo e serviamo noi: tu fai il padrone di casa, non il cameriere",
  "Niente pacchetti standard: ogni evento ha la sua proposta",
];

const EVENT_OPTIONS = [
  "Compleanno",
  "Festa privata",
  "Evento aziendale",
  "Ospiti B&B / casa vacanze",
  "Occasione speciale",
  "Altro",
];

export default function Eventi() {
  useSeo({
    title: "Eventi e catering privati a Rimini | Brunch & Bottega",
    description: "Catering per compleanni, feste private, piccoli eventi aziendali e ospiti di B&B a Rimini. Cucina fatta a mano, su misura e senza buffet tristi.",
    path: "/eventi",
  });

  return (
    <>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 sm:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-testid="eventi-hero">
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: "easeOut" }}>
          <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-5">Eventi & Catering</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.1] text-ink">
            Piccoli eventi, <em className="text-forest">grandi tavolate.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg leading-relaxed text-stone-600 max-w-xl">
            Cuciniamo per compleanni, feste private, piccoli eventi aziendali e ospiti di B&B e case vacanza.
            Pochi coperti, tanta cura: è il nostro formato preferito.
          </p>
        </motion.div>
        <motion.div className="lg:col-span-6" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }}>
          <div className="rounded-3xl overflow-hidden shadow-xl shadow-forest/10 border border-stone-200/60">
            <img
              src="/images/eventi-table.jpg"
              alt="Tavola apparecchiata con caffè e piatti fatti in casa per un piccolo evento"
              className="w-full h-72 sm:h-96 object-cover"
              fetchPriority="high"
              data-testid="eventi-hero-image"
            />
          </div>
        </motion.div>
      </section>

      <section className="bg-sand/70 border-y border-stone-200/60" data-testid="eventi-occasions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink">
            Per cosa ci chiamano di solito
          </motion.h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {OCCASIONS.map((o, i) => (
              <motion.article key={o.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
                className="bg-white/80 border border-stone-200/80 rounded-2xl p-7 shadow-sm hover:shadow-md hover:border-forest/30 transition-all"
                data-testid={`event-card-${i}`}>
                <o.icon size={28} className="text-terracotta" aria-hidden="true" />
                <h3 className="font-display text-xl mt-4 text-ink">{o.title}</h3>
                <p className="mt-2 text-sm sm:text-base leading-relaxed text-stone-600">{o.text}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="eventi-approach">
        <motion.h2 {...fadeUp} className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ink max-w-2xl">
          Come lavoriamo
        </motion.h2>
        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
          {APPROACH.map((a, i) => (
            <motion.li key={a} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="flex items-start gap-3 text-base text-stone-700" data-testid={`approach-item-${i}`}>
              <Check size={20} className="text-forest shrink-0 mt-0.5" aria-hidden="true" />
              {a}
            </motion.li>
          ))}
        </ul>
      </section>

      <section className="bg-forest" data-testid="eventi-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <motion.div {...fadeUp} className="lg:col-span-5 text-cream">
            <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-4">Facciamo due conti</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.2]">
              Organizziamo il tuo evento.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-cream/75">
              Dicci quando, dove e per quante persone. Al resto — menu, proposta, logistica —
              pensiamo insieme, con calma e senza impegno.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-7 bg-cream rounded-3xl p-6 sm:p-10 shadow-xl">
            <InquiryForm
              kind="evento"
              variant="event"
              eventOptions={EVENT_OPTIONS}
              submitLabel="Organizziamo il tuo evento"
              testIdPrefix="eventi"
            />
          </motion.div>
        </div>
      </section>
    </>
  );
}
